import { useEffect, useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Loader2,
  Package,
  User,
  Navigation,
  ShieldCheck,
  CheckCircle2,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import ReactCountryFlag from 'react-country-flag';
import { loadGoogleMapsPlaces } from '@/shared/lib/googleMapsPlaces';
import { getCurrentLanguage, changeLanguage, LANGUAGES, type Language } from '@/shared/i18n';
import env from '@/shared/config/env';
import {
  COUNTRY_PHONES,
  getCountryByCode,
  validatePhone,
} from '@/shared/utils/phone-validation';
import { ROUTES } from '@/app/routes/routes.config';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  description: string;
}

interface DropdownPos {
  top: number;
  left: number;
  width: number;
}

// ── Google Maps helpers ────────────────────────────────────────────────────────

function getGoogleSuggestions(query: string, country: string[]): Promise<PlaceSuggestion[]> {
  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const googleRef = (window as any).google;
    if (!googleRef?.maps?.places?.AutocompleteService) { resolve([]); return; }
    const service = new googleRef.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: query, componentRestrictions: { country }, types: ['geocode'] },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (predictions: any[] | null, status: string) => {
        const OK = googleRef?.maps?.places?.PlacesServiceStatus?.OK ?? 'OK';
        if (status !== OK || !Array.isArray(predictions)) { resolve([]); return; }
        resolve(predictions.map((p) => ({
          placeId: String(p.place_id || ''),
          mainText: String(p.structured_formatting?.main_text || p.description || ''),
          secondaryText: String(p.structured_formatting?.secondary_text || ''),
          description: String(p.description || ''),
        })));
      },
    );
  });
}

function resolvePlace(
  placeId: string,
  cb: (r: { address: string; city: string; state: string; zip: string; lat: number | null; lng: number | null }) => void,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const googleRef = (window as any).google;
  if (!googleRef?.maps?.Geocoder) return;
  const geocoder = new googleRef.maps.Geocoder();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  geocoder.geocode({ placeId }, (results: any[], status: string) => {
    if (status !== 'OK' || !results?.[0]) return;
    const place = results[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const components: any[] = place.address_components || [];
    const find = (type: string) => components.find((c) => c.types?.includes(type))?.long_name || '';
    cb({
      address: place.formatted_address || '',
      city: find('locality') || find('sublocality') || '',
      state: find('administrative_area_level_1'),
      zip: find('postal_code'),
      lat: place.geometry?.location?.lat() ?? null,
      lng: place.geometry?.location?.lng() ?? null,
    });
  });
}

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z
  .object({
    isInternational: z.boolean(),
    hondurasDropoffContactName: z.string().trim().max(255).optional().or(z.literal('')),
    hondurasDropoffContactCountryCode: z.string().trim().length(2).optional().or(z.literal('')),
    hondurasDropoffContactPhonePrefix: z.string().trim().regex(/^\+[0-9]{1,5}$/).optional().or(z.literal('')),
    hondurasDropoffContactPhoneNumber: z.string().trim().max(20).optional().or(z.literal('')),
    hondurasDropoffContactEmail: z.string().trim().email().max(255).optional().or(z.literal('')),
    originAddress: z.string().trim().min(8, 'Dirección de origen requerida').max(255),
    destinationContact: z.string().trim().min(2).max(255),
    destinationContactNumber: z.string().trim().regex(/^[+0-9()\-\s]{7,20}$/),
    destinationAddress: z.string().trim().min(8).max(255),
    destinationCity: z.string().trim().min(2).max(100).regex(/^[A-Za-zÀ-ÿ0-9\s.,-]+$/),
    destinationState: z.string().trim().min(2).max(100).regex(/^[A-Za-zÀ-ÿ0-9\s.,-]+$/),
    destinationZipCode: z.string().trim().max(30).optional(),
    destinationLatitude: z.number().optional(),
    destinationLongitude: z.number().optional(),
    weight: z.coerce.number().min(0.1).max(150),
    name: z.string().trim().min(3).max(255),
    description: z.string().max(2000).optional(),
    originInstructions: z.string().max(255).optional(),
    coverageFee: z.union([z.literal(0), z.literal(5), z.literal(10), z.literal(15)]).default(0),
    isTechnologyPackage: z.boolean().default(false),
  })
  .superRefine((values, ctx) => {
    if (values.isInternational) {
      if (!values.hondurasDropoffContactName || values.hondurasDropoffContactName.trim().length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Nombre requerido', path: ['hondurasDropoffContactName'] });
      }
      if (!values.hondurasDropoffContactPhoneNumber || values.hondurasDropoffContactPhoneNumber.trim().length < 7) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Teléfono requerido', path: ['hondurasDropoffContactPhoneNumber'] });
      } else if (
        values.hondurasDropoffContactCountryCode &&
        !validatePhone(values.hondurasDropoffContactPhoneNumber, values.hondurasDropoffContactCountryCode)
      ) {
        const mask = getCountryByCode(values.hondurasDropoffContactCountryCode)?.mask || '';
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: mask ? `Formato inválido. Ejemplo: ${mask}` : 'Formato inválido',
          path: ['hondurasDropoffContactPhoneNumber'],
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

// ── Language → country code ────────────────────────────────────────────────────

const LANG_COUNTRY: Record<Language, string> = { es: 'ES', en: 'US', fr: 'FR' };

// ── HNL pricing (loaded from DB via public endpoint) ─────────────────────────
// Se carga en el componente con useEffect → fetch /public/te-lo-llevo/pricing

// ── Style tokens ───────────────────────────────────────────────────────────────

const YELLOW = '#ffdb00';
const PINK   = '#ef3090';
const NAVY   = '#1e416c';

const cardShadow = { boxShadow: `4px 6px 0 0 ${YELLOW}` };
const inputClass =
  'w-full rounded-xl border-2 border-black px-4 py-3 text-sm text-gray-900 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300 transition-all duration-150';
const labelClass = 'block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider';

// ── Section wrapper ────────────────────────────────────────────────────────────

function Section({
  icon,
  title,
  headerBg,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  headerBg: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border-2 border-black bg-white overflow-hidden" style={cardShadow}>
      <div className={`flex items-center gap-3 px-5 py-3.5 border-b-2 border-black ${headerBg}`}>
        <div className="flex-shrink-0">{icon}</div>
        <p className="text-xs font-black uppercase tracking-widest">{title}</p>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function PublicShipmentFormPage() {
  const { t } = useTranslation();

  const isInternational = true;
  const [mapsReady, setMapsReady] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentLang, setCurrentLang] = useState<Language>(getCurrentLanguage());
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement | null>(null);
  const [hnlPrices, setHnlPrices] = useState<{ lb: number; hnl: number }[]>([]);

  // ── Draft tracking ────────────────────────────────────────────────────────
  const draftTokenRef = useRef<string | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Crear el draft al montar el componente
  useEffect(() => {
    fetch(`${env.apiUrl}/public/shipment-requests/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flowType: 'international' }),
    })
      .then((r) => r.json())
      .then((data) => { if (data?.draftToken) draftTokenRef.current = data.draftToken; })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${env.apiUrl}/public/te-lo-llevo/pricing`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data?.prices)) setHnlPrices(data.prices); })
      .catch(() => {});
  }, []);

  const handleLanguageChange = (lang: Language) => {
    changeLanguage(lang);
    setCurrentLang(lang);
    setLangOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Origin autocomplete
  const [originText, setOriginText] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState<PlaceSuggestion[]>([]);
  const [originLoading, setOriginLoading] = useState(false);
  const [originOpen, setOriginOpen] = useState(false);
  const [originPos, setOriginPos] = useState<DropdownPos | null>(null);
  const originRef = useRef<HTMLInputElement | null>(null);
  const [originCity, setOriginCity] = useState('');
  const [originState, setOriginState] = useState('');

  // Destination autocomplete
  const [destText, setDestText] = useState('');
  const [destSuggestions, setDestSuggestions] = useState<PlaceSuggestion[]>([]);
  const [destLoading, setDestLoading] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [destPos, setDestPos] = useState<DropdownPos | null>(null);
  const destRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      isInternational: true,
      hondurasDropoffContactCountryCode: 'HN',
      hondurasDropoffContactPhonePrefix: '+504',
      coverageFee: 0,
      isTechnologyPackage: false,
    },
  });

  const selectedCountryCode = watch('hondurasDropoffContactCountryCode') ?? 'HN';
  const selectedCoverageFee = watch('coverageFee');
  const weightValue = Number(watch('weight')) || 0;
  const selectedCountry = getCountryByCode(selectedCountryCode) ?? COUNTRY_PHONES[0];

  // Auto-save del draft: se dispara 2 segundos después del último cambio en el form
  useEffect(() => {
    const subscription = watch((values) => {
      if (!draftTokenRef.current) return;
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

      draftTimerRef.current = setTimeout(() => {
        // Determinar hasta qué sección llegó el usuario
        let lastSection = 'started';
        const hasSender =
          (values.hondurasDropoffContactName?.trim().length ?? 0) >= 2 &&
          (values.hondurasDropoffContactPhoneNumber?.trim().length ?? 0) >= 7;
        const hasOrigin = (values.originAddress?.trim().length ?? 0) >= 8;
        const hasDestination =
          (values.destinationContact?.trim().length ?? 0) >= 2 &&
          (values.destinationAddress?.trim().length ?? 0) >= 8 &&
          (values.destinationCity?.trim().length ?? 0) >= 2;
        const hasPackage =
          Number(values.weight) >= 0.1 &&
          (values.name?.trim().length ?? 0) >= 2;

        if (hasSender) lastSection = 'sender';
        if (hasSender && hasOrigin) lastSection = 'origin';
        if (hasSender && hasOrigin && hasDestination) lastSection = 'destination';
        if (hasSender && hasOrigin && hasDestination && hasPackage) lastSection = 'package';

        const payload: Record<string, unknown> = { lastSection };
        if (values.hondurasDropoffContactName) payload.senderName = values.hondurasDropoffContactName;
        if (values.hondurasDropoffContactPhonePrefix) payload.senderPhonePrefix = values.hondurasDropoffContactPhonePrefix;
        if (values.hondurasDropoffContactPhoneNumber) payload.senderPhone = values.hondurasDropoffContactPhoneNumber;
        if (values.hondurasDropoffContactEmail) payload.senderEmail = values.hondurasDropoffContactEmail;
        if (values.originAddress) payload.originAddress = values.originAddress;
        if (values.destinationContact) payload.destinationContact = values.destinationContact;
        if (values.destinationContactNumber) payload.destinationContactNumber = values.destinationContactNumber;
        if (values.destinationAddress) payload.destinationAddress = values.destinationAddress;
        if (values.destinationCity) payload.destinationCity = values.destinationCity;
        if (values.destinationState) payload.destinationState = values.destinationState;
        if (values.destinationZipCode) payload.destinationZipCode = values.destinationZipCode;
        if (values.destinationLatitude != null) payload.destinationLatitude = values.destinationLatitude;
        if (values.destinationLongitude != null) payload.destinationLongitude = values.destinationLongitude;
        if (Number(values.weight) >= 0.1) payload.weight = Number(values.weight);
        if (values.name) payload.name = values.name;
        if (values.description) payload.description = values.description;
        if (values.originInstructions) payload.originInstructions = values.originInstructions;
        if (values.coverageFee != null) payload.coverageFee = values.coverageFee;
        if (values.isTechnologyPackage != null) payload.isTechnologyPackage = values.isTechnologyPackage;

        fetch(`${env.apiUrl}/public/shipment-requests/draft/${draftTokenRef.current}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }, 2000);
    });

    return () => {
      subscription.unsubscribe();
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [watch]);

  useEffect(() => {
    if (!env.googleMapsApiKey) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).google?.maps?.places?.AutocompleteService) { setMapsReady(true); return; }
    loadGoogleMapsPlaces(env.googleMapsApiKey, getCurrentLanguage()).then(() => setMapsReady(true)).catch(() => {});
  }, []);

useEffect(() => {
    if (originText.trim().length < 3) { setOriginSuggestions([]); setOriginLoading(false); return; }
    setOriginLoading(true); setOriginOpen(true);
    if (originRef.current) {
      const r = originRef.current.getBoundingClientRect();
      setOriginPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
    }
    const countries = isInternational ? ['us', 'hn'] : ['us'];
    const t2 = setTimeout(async () => {
      setOriginSuggestions(await getGoogleSuggestions(originText, countries));
      setOriginLoading(false);
    }, 350);
    return () => clearTimeout(t2);
  }, [originText, mapsReady, isInternational]);

  useEffect(() => {
    if (destText.trim().length < 3) { setDestSuggestions([]); setDestLoading(false); return; }
    setDestLoading(true); setDestOpen(true);
    if (destRef.current) {
      const r = destRef.current.getBoundingClientRect();
      setDestPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
    }
    const t2 = setTimeout(async () => {
      setDestSuggestions(await getGoogleSuggestions(destText, ['us']));
      setDestLoading(false);
    }, 350);
    return () => clearTimeout(t2);
  }, [destText, mapsReady]);

  const handleOriginSelect = useCallback((s: PlaceSuggestion) => {
    setOriginOpen(false); setOriginText(s.description);
    setValue('originAddress', s.description, { shouldValidate: true, shouldDirty: true });
    resolvePlace(s.placeId, ({ address, city, state, lat, lng }) => {
      const final = address || s.description;
      setOriginText(final);
      setValue('originAddress', final, { shouldValidate: true, shouldDirty: true });
      setOriginCity(city || ''); setOriginState(state || '');
      if (lat != null) setValue('destinationLatitude', lat);
      if (lng != null) setValue('destinationLongitude', lng);
    });
  }, [setValue]);

  const handleDestSelect = useCallback((s: PlaceSuggestion) => {
    setDestOpen(false); setDestText(s.description);
    setValue('destinationAddress', s.description, { shouldValidate: true, shouldDirty: true });
    resolvePlace(s.placeId, ({ address, city, state, zip, lat, lng }) => {
      const final = address || s.description;
      setDestText(final);
      setValue('destinationAddress', final, { shouldValidate: true, shouldDirty: true });
      if (city) setValue('destinationCity', city, { shouldValidate: true });
      if (state) setValue('destinationState', state, { shouldValidate: true });
      if (zip) setValue('destinationZipCode', zip);
      if (lat != null) setValue('destinationLatitude', lat);
      if (lng != null) setValue('destinationLongitude', lng);
    });
  }, [setValue]);

  const renderDropdown = (
    open: boolean, pos: DropdownPos | null, loading: boolean,
    suggestions: PlaceSuggestion[], onSelect: (s: PlaceSuggestion) => void,
  ) => {
    if (!open || !pos) return null;
    return createPortal(
      <div
        style={{ position: 'absolute', top: pos.top + 6, left: pos.left, width: pos.width, zIndex: 9999 }}
        className="bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-52 overflow-y-auto"
      >
        {loading && (
          <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando...
          </div>
        )}
        {!loading && suggestions.length === 0 && (
          <div className="px-4 py-3 text-sm text-gray-400">Sin resultados</div>
        )}
        {suggestions.map((s) => (
          <button
            key={s.placeId} type="button"
            onMouseDown={(e) => { e.preventDefault(); onSelect(s); }}
            className="flex items-start gap-3 w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors"
          >
            <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span>
              <span className="font-medium text-gray-800">{s.mainText}</span>
              {s.secondaryText && <span className="text-gray-400">, {s.secondaryText}</span>}
            </span>
          </button>
        ))}
      </div>,
      document.body,
    );
  };

  const { ref: originRhfRef, ...originRegisterRest } = register('originAddress');
  const { ref: destRhfRef, ...destRegisterRest } = register('destinationAddress');

  const onSubmit = async (data: FormValues) => {
    setSubmitError('');
    try {
      const payload = {
        flowType: data.isInternational ? 'international' : 'domestic',
        senderName: data.hondurasDropoffContactName || undefined,
        senderPhonePrefix: data.hondurasDropoffContactPhonePrefix || undefined,
        senderPhone: data.hondurasDropoffContactPhoneNumber || undefined,
        senderEmail: data.hondurasDropoffContactEmail || undefined,
        originAddress: data.originAddress,
        destinationContact: data.destinationContact,
        destinationContactNumber: data.destinationContactNumber,
        destinationAddress: data.destinationAddress,
        destinationCity: data.destinationCity,
        destinationState: data.destinationState,
        destinationZipCode: data.destinationZipCode || undefined,
        destinationLatitude: data.destinationLatitude,
        destinationLongitude: data.destinationLongitude,
        weight: data.weight,
        name: data.name,
        description: data.description?.trim() || undefined,
        originInstructions: data.originInstructions?.trim() || undefined,
        coverageFee: data.coverageFee,
        isTechnologyPackage: data.coverageFee === 15 ? true : (data.isTechnologyPackage ?? false),
        // Vincula el draft progresivo con la solicitud completada
        draftToken: draftTokenRef.current ?? undefined,
      };

      const res = await fetch(`${env.apiUrl}/public/shipment-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error();
      setSubmitted(true);
      reset();
      setOriginText(''); setDestText(''); setOriginCity(''); setOriginState('');
    } catch {
      setSubmitError(t('publicShipmentForm.error'));
    }
  };

  // ── Success ───────────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: PINK }}>
        <div className="bg-white rounded-2xl border-2 border-black p-10 max-w-sm w-full text-center" style={cardShadow}>
          <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-6 border-2 border-black" style={{ background: YELLOW }}>
            <CheckCircle2 className="h-10 w-10 text-black" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-3">{t('publicShipmentForm.successTitle')}</h2>
          <p className="text-sm text-gray-600 leading-relaxed">{t('publicShipmentForm.successMsg')}</p>
        </div>
      </div>
    );
  }

  // ── Main ──────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ background: '#f5f5f5' }}>

      {/* ── Navbar ────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b-2 border-black" style={{ background: NAVY }}>
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <img src="/te_lo_llevo.webp" alt="Te Lo Llevo" className="h-11 w-auto object-contain" />

          {/* Language dropdown */}
          <div ref={langRef} className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center gap-2 px-3 py-2 rounded-full border-2 border-white/30 text-xs font-bold text-white hover:border-white transition-all"
            >
              <ReactCountryFlag countryCode={LANG_COUNTRY[currentLang]} svg style={{ width: '1em', height: '1em', borderRadius: '2px' }} />
              {LANGUAGES[currentLang].label}
              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white border-2 border-black rounded-xl z-50 overflow-hidden py-1" style={{ boxShadow: '3px 3px 0 0 #000' }}>
                {(Object.entries(LANGUAGES) as [Language, { label: string }][]).map(([code, { label }]) => (
                  <button
                    key={code} type="button"
                    onClick={() => handleLanguageChange(code)}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-colors',
                      currentLang === code ? 'text-black' : 'text-gray-600 hover:bg-gray-50',
                    ].join(' ')}
                    style={currentLang === code ? { background: YELLOW } : {}}
                  >
                    <ReactCountryFlag countryCode={LANG_COUNTRY[code]} svg style={{ width: '1.1em', height: '1.1em', borderRadius: '2px' }} />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <div className="border-b-2 border-black pt-10 pb-16 px-4 text-center" style={{ background: PINK }}>
        <div className="inline-block mb-3 px-4 py-1 rounded-full border-2 border-black text-xs font-black uppercase tracking-widest text-black" style={{ background: YELLOW }}>
          Te Lo Llevo — Pa' los Estados
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-2" style={{ textShadow: '2px 2px 0 #000' }}>
          {t('publicShipmentForm.title')}
        </h1>
        <p className="text-sm text-white/80 max-w-sm mx-auto font-medium">{t('publicShipmentForm.subtitle')}</p>
      </div>

      {/* ── Form card ─────────────────────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 -mt-6 pb-14">
        <div className="bg-white rounded-2xl border-2 border-black overflow-hidden" style={cardShadow}>

          {/* Form sections */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
            <input type="hidden" {...register('isInternational')} value={isInternational ? 'true' : 'false'} />

            {/* ── SENDER ────────────────────────────────────────────────── */}
            <Section
              icon={<div className="h-7 w-7 rounded-lg border-2 border-black flex items-center justify-center bg-gray-100"><User className="h-4 w-4 text-gray-600" /></div>}
              title={t('b2b.packages.createModal.senderTitle', 'Sender (Persona que envía)')}
              headerBg="bg-gray-100 text-gray-800"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('b2b.packages.createModal.hondurasDropoffName')} <span className="text-red-500">*</span></label>
                  <input {...register('hondurasDropoffContactName')} className={inputClass} placeholder="Ej: María García" />
                  {errors.hondurasDropoffContactName && <p className="text-xs text-red-500 mt-1.5">{errors.hondurasDropoffContactName.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.hondurasDropoffCountry')} <span className="text-red-500">*</span></label>
                  <select
                    className={inputClass}
                    value={selectedCountry.code}
                    onChange={(e) => {
                      const c = getCountryByCode(e.target.value);
                      setValue('hondurasDropoffContactCountryCode', e.target.value, { shouldValidate: true, shouldDirty: true });
                      if (c) setValue('hondurasDropoffContactPhonePrefix', c.prefix, { shouldValidate: true, shouldDirty: true });
                    }}
                  >
                    {COUNTRY_PHONES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.prefix})</option>)}
                  </select>
                  <input type="hidden" {...register('hondurasDropoffContactCountryCode')} />
                  <input type="hidden" {...register('hondurasDropoffContactPhonePrefix')} />
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.hondurasDropoffPhone')} <span className="text-red-500">*</span></label>
                  <input {...register('hondurasDropoffContactPhoneNumber')} className={inputClass} placeholder={selectedCountry.mask} />
                  {errors.hondurasDropoffContactPhoneNumber && <p className="text-xs text-red-500 mt-1.5">{errors.hondurasDropoffContactPhoneNumber.message}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('b2b.packages.createModal.hondurasDropoffEmail')} <span className="text-gray-400 font-medium normal-case">(opcional)</span></label>
                  <input type="email" {...register('hondurasDropoffContactEmail')} className={inputClass} placeholder="correo@ejemplo.com" />
                  {errors.hondurasDropoffContactEmail && <p className="text-xs text-red-500 mt-1.5">{errors.hondurasDropoffContactEmail.message}</p>}
                </div>
              </div>
            </Section>

            {/* ── ORIGEN ────────────────────────────────────────────────── */}
            <Section
              icon={<div className="h-7 w-7 rounded-lg border-2 border-black flex items-center justify-center" style={{ background: '#c7f0ff' }}><Navigation className="h-4 w-4 text-black" /></div>}
              title={t('b2b.packages.createModal.originSection', 'Dirección de origen')}
              headerBg="text-black"
            >
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.originAddress', 'Dirección')} <span className="text-red-500">*</span></label>
                  <input
                    ref={(el) => { originRhfRef(el); originRef.current = el; }}
                    {...originRegisterRest}
                    value={originText}
                    onChange={(e) => { setOriginText(e.target.value); setValue('originAddress', e.target.value, { shouldValidate: false }); if (e.target.value.length < 3) setOriginOpen(false); }}
                    onFocus={() => { if (originText.length >= 3 && originSuggestions.length > 0) setOriginOpen(true); }}
                    onBlur={() => setTimeout(() => setOriginOpen(false), 200)}
                    className={inputClass}
                    placeholder={t('b2b.packages.createModal.originAddressPlaceholder', 'Ej: 123 Main St, Miami')}
                    autoComplete="off"
                  />
                  {errors.originAddress && <p className="text-xs text-red-500 mt-1.5">{errors.originAddress.message}</p>}
                </div>
                {(originCity || originState) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>{t('b2b.packages.createModal.originCity', 'Ciudad')}</label>
                      <input value={originCity} onChange={(e) => setOriginCity(e.target.value)} className={inputClass} placeholder="Ej: Miami" />
                    </div>
                    <div>
                      <label className={labelClass}>{t('b2b.packages.createModal.originState', 'Estado')}</label>
                      <input value={originState} onChange={(e) => setOriginState(e.target.value)} className={inputClass} placeholder="Ej: Florida" />
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* ── DESTINATARIO ──────────────────────────────────────────── */}
            <Section
              icon={<div className="h-7 w-7 rounded-lg border-2 border-black flex items-center justify-center" style={{ background: PINK }}><User className="h-4 w-4 text-white" /></div>}
              title={t('b2b.packages.createModal.recipientSection', 'Destinatario')}
              headerBg="text-black"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.destinationContact')} <span className="text-red-500">*</span></label>
                  <input {...register('destinationContact')} className={inputClass} placeholder={t('b2b.packages.createModal.destinationContactPlaceholder', 'Ej: Juan Pérez')} />
                  {errors.destinationContact && <p className="text-xs text-red-500 mt-1.5">{errors.destinationContact.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.phone')} <span className="text-red-500">*</span></label>
                  <input {...register('destinationContactNumber')} className={inputClass} placeholder="+1 305 555 1234" />
                  {errors.destinationContactNumber && <p className="text-xs text-red-500 mt-1.5">{errors.destinationContactNumber.message}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('b2b.packages.createModal.destinationAddress')} <span className="text-red-500">*</span></label>
                  <input
                    ref={(el) => { destRhfRef(el); destRef.current = el; }}
                    {...destRegisterRest}
                    value={destText}
                    onChange={(e) => { setDestText(e.target.value); setValue('destinationAddress', e.target.value, { shouldValidate: false }); if (e.target.value.length < 3) setDestOpen(false); }}
                    onFocus={() => { if (destText.length >= 3 && destSuggestions.length > 0) setDestOpen(true); }}
                    onBlur={() => setTimeout(() => setDestOpen(false), 200)}
                    className={inputClass}
                    placeholder="Street, suite/apartment, reference"
                    autoComplete="off"
                  />
                  {errors.destinationAddress && <p className="text-xs text-red-500 mt-1.5">{errors.destinationAddress.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.destinationCity')} <span className="text-red-500">*</span></label>
                  <input {...register('destinationCity')} className={inputClass} placeholder="Ej: Miami" />
                  {errors.destinationCity && <p className="text-xs text-red-500 mt-1.5">{errors.destinationCity.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.destinationState')} <span className="text-red-500">*</span></label>
                  <input {...register('destinationState')} className={inputClass} placeholder="Ej: Florida" />
                  {errors.destinationState && <p className="text-xs text-red-500 mt-1.5">{errors.destinationState.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>ZIP code <span className="text-gray-400 font-medium normal-case">(opcional)</span></label>
                  <input {...register('destinationZipCode')} className={inputClass} placeholder="Ej: 33101" />
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.weight')} <span className="text-red-500">*</span></label>
                  <input type="number" step="0.1" min="0.1" {...register('weight')} className={inputClass} placeholder="Ej: 3.5" />
                  {errors.weight && <p className="text-xs text-red-500 mt-1.5">{errors.weight.message}</p>}

                  {/* Price for entered weight */}
                  {weightValue > 0 && (() => {
                    const match = hnlPrices.find(({ lb }) => lb === Math.round(weightValue));
                    return match ? (
                      <p className="mt-2 text-sm font-black" style={{ color: PINK }}>
                        {t('publicShipmentForm.estimatedPrice', { price: match.hnl.toLocaleString() })}
                      </p>
                    ) : weightValue > 10 ? (
                      <p className="mt-2 text-sm font-semibold text-gray-500">
                        {t('publicShipmentForm.priceConsult')}
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>
            </Section>

            {/* ── PAQUETE ───────────────────────────────────────────────── */}
            <Section
              icon={<div className="h-7 w-7 rounded-lg border-2 border-black flex items-center justify-center" style={{ background: YELLOW }}><Package className="h-4 w-4 text-black" /></div>}
              title={t('b2b.packages.createModal.packageInfoSection')}
              headerBg="text-black"
            >
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.packageName')} <span className="text-red-500">*</span></label>
                  <input {...register('name')} className={inputClass} placeholder={t('b2b.packages.createModal.packageNamePlaceholder', 'Ej: Ropa, Electrónicos...')} />
                  {errors.name && <p className="text-xs text-red-500 mt-1.5">{errors.name.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.description')} <span className="text-gray-400 font-medium normal-case">(opcional)</span></label>
                  <textarea {...register('description')} rows={3} className={`${inputClass} resize-y min-h-[4.5rem]`} placeholder={t('b2b.packages.createModal.descriptionPlaceholder', 'Descripción del contenido')} />
                </div>
                <div>
                  <label className={labelClass}>{t('b2b.packages.createModal.instructions')} <span className="text-gray-400 font-medium normal-case">(opcional)</span></label>
                  <textarea {...register('originInstructions')} rows={3} className={`${inputClass} resize-y min-h-[4.5rem]`} placeholder={t('b2b.packages.createModal.instructionsPlaceholder', 'Instrucciones para el recogedor')} />
                </div>
              </div>
            </Section>

            {/* ── COBERTURA ─────────────────────────────────────────────── */}
            <Section
              icon={<div className="h-7 w-7 rounded-lg border-2 border-black flex items-center justify-center bg-white"><ShieldCheck className="h-4 w-4 text-black" /></div>}
              title={t('b2b.packages.createModal.coverage.title')}
              headerBg="text-black"
            >
              <div className="space-y-3">
                <p className="text-xs text-gray-600 font-medium">{t('b2b.packages.createModal.coverage.subtitle')}</p>
                <div className="grid grid-cols-2 gap-2.5">
                  {([
                    { fee: 0,  max: 100, labelKey: 'coverage.tierNone',     techOnly: false },
                    { fee: 5,  max: 200, labelKey: 'coverage.tierBasic',    techOnly: false },
                    { fee: 10, max: 500, labelKey: 'coverage.tierStandard', techOnly: false },
                    { fee: 15, max: 800, labelKey: 'coverage.tierTech',     techOnly: true  },
                  ] as const).map(({ fee, max, labelKey, techOnly }) => {
                    const active = selectedCoverageFee === fee;
                    return (
                      <button
                        key={fee} type="button"
                        onClick={() => { setValue('coverageFee', fee, { shouldValidate: true }); setValue('isTechnologyPackage', fee === 15); }}
                        className="relative flex flex-col items-start rounded-xl border-2 border-black px-4 py-3 text-left transition-all duration-150"
                        style={active ? { background: YELLOW, boxShadow: '3px 3px 0 0 #000' } : { background: '#fff' }}
                      >
                        {techOnly && (
                          <span className="absolute top-2 right-2 text-[10px] font-black border border-black rounded-full px-1.5 py-0.5" style={{ background: PINK, color: '#fff' }}>
                            {t('b2b.packages.createModal.coverage.techBadge')}
                          </span>
                        )}
                        <span className="text-xs font-black text-black">{t(`b2b.packages.createModal.${labelKey}`)}</span>
                        <span className="text-[11px] mt-0.5 text-gray-600 font-medium">{t('b2b.packages.createModal.coverage.maxLabel', { amount: max })}</span>
                        <span className="text-sm font-black mt-2 text-black">{fee === 0 ? t('b2b.packages.createModal.coverage.free') : `+$${fee}`}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedCoverageFee > 0 && (
                  <p className="text-xs text-black font-medium rounded-xl px-4 py-2.5 border-2 border-black" style={{ background: YELLOW }}
                    dangerouslySetInnerHTML={{ __html: t('b2b.packages.createModal.coverage.selectedInfo', { max: selectedCoverageFee === 5 ? 200 : selectedCoverageFee === 10 ? 500 : 800, fee: selectedCoverageFee }) }}
                  />
                )}
                {selectedCoverageFee === 0 && (
                  <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-4 py-2.5 border-2 border-gray-200 font-medium"
                    dangerouslySetInnerHTML={{ __html: t('b2b.packages.createModal.coverage.noCoverageInfo') }}
                  />
                )}
              </div>
            </Section>

            {/* Error */}
            {submitError && (
              <div className="border-2 border-red-400 rounded-xl px-4 py-3 bg-red-50">
                <p className="text-sm text-red-600 font-semibold">{submitError}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 rounded-full border-2 border-black text-black text-sm font-black disabled:opacity-50 transition-all duration-150 flex items-center justify-center gap-2"
              style={{ background: YELLOW, boxShadow: '4px 4px 0 0 #000' }}
            >
              {isSubmitting
                ? <><Loader2 className="h-4 w-4 animate-spin" />{t('common.loading')}</>
                : <>{t('publicShipmentForm.submit')}<ArrowRight className="h-4 w-4" /></>}
            </button>

            <p className="text-center text-xs text-gray-500 pb-2 font-medium">
              {t('publicShipmentForm.alreadyHaveAccount')}{' '}
              <a href={ROUTES.AUTH.LOGIN} className="font-black underline" style={{ color: PINK }}>
                {t('publicShipmentForm.loginLink')}
              </a>
            </p>
          </form>
        </div>
      </div>

      {renderDropdown(originOpen, originPos, originLoading, originSuggestions, handleOriginSelect)}
      {renderDropdown(destOpen, destPos, destLoading, destSuggestions, handleDestSelect)}
    </div>
  );
}
